import { useState } from 'react';
import { partiesApi } from '../../api/parties';
import type { Party } from '@coreledger/shared-types';

interface Props {
  partySearch: string;
  onSearchChange: (val: string) => void;
  parties: Party[];
  onSelectParty: (p: Party) => void;
  partyGstin: string;
  onGstinChange: (val: string) => void;
  partyAddress: string;
  onAddressChange: (val: string) => void;
  docType: string;
  consigneeName?: string;
  consigneeGstin?: string;
  consigneeAddress?: string;
  onConsigneeChange: (k: 'consigneeName' | 'consigneeGstin' | 'consigneeAddress', val: string) => void;
}

export function PartySelector({
  partySearch, onSearchChange, parties, onSelectParty,
  partyGstin, onGstinChange, partyAddress, onAddressChange,
  docType, consigneeName, consigneeGstin, consigneeAddress, onConsigneeChange,
}: Props) {
  const [showDD, setShowDD] = useState(false);
  const [showInline, setShowInline] = useState(false);
  const [inlineForm, setInlineForm] = useState({ name: '', gstin: '', type: 'customer' as 'customer' | 'vendor' | 'both' });
  const [inlineSaving, setInlineSaving] = useState(false);

  const filtered = parties.filter(p => p.name.toLowerCase().includes(partySearch.toLowerCase())).slice(0, 8);

  async function handleCreateParty() {
    if (!inlineForm.name.trim()) return;
    setInlineSaving(true);
    try {
      const created = await partiesApi.create({ name: inlineForm.name.trim(), gstin: inlineForm.gstin || undefined, type: inlineForm.type });
      onSelectParty(created as Party);
      setShowInline(false);
      setInlineForm({ name: '', gstin: '', type: 'customer' });
    } catch { alert('Failed to create party'); }
    finally { setInlineSaving(false); }
  }

  const showConsignee = docType === 'tax_invoice' || docType === 'bill_of_supply' || docType === 'lorry_receipt';

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="card-header"><span style={{ fontWeight: 700 }}>Party / Customer</span></div>
      <div className="card-body">
        <div className="form-row form-row-2">
          <label>Party / Customer *
            <div style={{ position: 'relative' }}>
              <input
                value={partySearch}
                onChange={e => { onSearchChange(e.target.value); setShowDD(true); }}
                onFocus={() => setShowDD(true)}
                onBlur={() => setTimeout(() => setShowDD(false), 200)}
                placeholder="Type to search parties…"
              />
              {showDD && (filtered.length > 0 || partySearch.length > 0) && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 50, boxShadow: 'var(--shadow-md)', maxHeight: 240, overflowY: 'auto' }}>
                  {filtered.map(p => (
                    <div key={p.id} onMouseDown={() => { onSelectParty(p); setShowDD(false); }}
                      style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                      {p.gstin && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>GSTIN: {p.gstin}</div>}
                      {p.city && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.city}, {p.state}</div>}
                    </div>
                  ))}
                  <div
                    onMouseDown={() => { setShowInline(true); setShowDD(false); setInlineForm(f => ({ ...f, name: partySearch })); }}
                    style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderTop: filtered.length > 0 ? '1px solid var(--border)' : undefined, background: 'var(--surface-alt)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-alt)')}>
                    + Create New Party{partySearch ? ` "${partySearch}"` : ''}
                  </div>
                </div>
              )}
              {showInline && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 60, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '1rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.75rem', color: 'var(--primary)' }}>Quick-Add Party</div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input value={inlineForm.name} onChange={e => setInlineForm(f => ({ ...f, name: e.target.value }))} placeholder="Party name *" autoFocus style={{ flex: 2 }} />
                    <input value={inlineForm.gstin} onChange={e => setInlineForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="GSTIN (optional)" maxLength={15} style={{ flex: 1, textTransform: 'uppercase' }} />
                    <select value={inlineForm.type} onChange={e => setInlineForm(f => ({ ...f, type: e.target.value as 'customer' | 'vendor' | 'both' }))} style={{ flex: 1 }}>
                      <option value="customer">Customer</option>
                      <option value="vendor">Vendor</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-ghost btn-sm" onMouseDown={() => setShowInline(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={inlineSaving || !inlineForm.name.trim()} onMouseDown={handleCreateParty}>
                      {inlineSaving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Add Party'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </label>
          <label>Party GSTIN<input value={partyGstin} onChange={e => onGstinChange(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15} style={{ textTransform: 'uppercase' }} /></label>
        </div>
        <div className="form-row">
          <label>Party Address<input value={partyAddress} onChange={e => onAddressChange(e.target.value)} placeholder="Street, City, State" /></label>
        </div>
        {showConsignee && (
          <details style={{ marginTop: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-2)', userSelect: 'none', padding: '0.25rem 0' }}>
              + Consignee / Ship-To Details (if different from billing party)
            </summary>
            <div style={{ marginTop: '0.75rem' }}>
              <div className="form-row form-row-3">
                <label>Consignee Name<input value={consigneeName ?? ''} onChange={e => onConsigneeChange('consigneeName', e.target.value)} placeholder="Consignee / Ship-To name" /></label>
                <label>Consignee GSTIN<input value={consigneeGstin ?? ''} onChange={e => onConsigneeChange('consigneeGstin', e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15} style={{ textTransform: 'uppercase' }} /></label>
                <label>Consignee Address<input value={consigneeAddress ?? ''} onChange={e => onConsigneeChange('consigneeAddress', e.target.value)} placeholder="Street, City, State" /></label>
              </div>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
